import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { pricingPlans } from '../mock';
import { Check, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

export default function Pricing() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const scrollToSection = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="pricing" className="py-28 md:py-40 bg-[#050A08] relative overflow-hidden" data-testid="pricing-section">
      <div className="absolute bottom-0 left-1/4 w-[600px] h-[500px] bg-emerald-500/[0.07] rounded-full blur-[160px] pointer-events-none" />

      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-12">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <span className="inline-block text-emerald-400 font-semibold text-sm uppercase tracking-[0.25em] mb-5">
            / Simple Pricing
          </span>
          <h2 className="font-display text-4xl md:text-6xl font-black text-white leading-[0.95] mb-6">
            One flat rate.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-lime-400">Zero surprises.</span>
          </h2>
          <p className="text-lg text-slate-400">
            No contracts. No hidden fees. Cancel or scale anytime as your needs change.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {pricingPlans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: index * 0.12 }}
              className={`relative rounded-[28px] ${plan.popular ? 'tracing-beam' : ''}`}
              data-testid={`pricing-card-${plan.id}`}
            >
              <div className={`relative z-10 h-full rounded-[26px] p-8 md:p-10 flex flex-col ${
                plan.popular
                  ? 'bg-[#0D1612] border border-emerald-500/20'
                  : 'bg-[#0D1612] border border-white/[0.06]'
              }`}>
                {plan.popular && (
                  <span className="inline-flex w-fit items-center px-4 py-1.5 rounded-full bg-emerald-500 text-[#04120C] text-xs font-bold uppercase tracking-wide mb-6">
                    Most Popular
                  </span>
                )}
                {!plan.popular && <div className="h-[38px] mb-6" />}

                <h3 className="font-display text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-slate-400 text-sm mb-8 min-h-[40px]">{plan.description}</p>

                <div className="flex items-baseline gap-2 mb-1">
                  <span className="font-display text-6xl font-black text-white">${plan.price}</span>
                  <span className="text-slate-500">/{plan.period}</span>
                </div>
                <div className="text-emerald-400 text-sm font-medium mb-8">{plan.hours}</div>

                <div className="h-px bg-white/[0.06] mb-8" />

                <ul className="space-y-4 mb-10 flex-1">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-emerald-400" />
                      </span>
                      <span className="text-slate-300 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => scrollToSection('#contact')}
                  data-testid={`pricing-cta-${plan.id}`}
                  className={`group w-full py-6 rounded-full font-bold transition-all duration-300 ${
                    plan.popular
                      ? 'bg-emerald-500 hover:bg-emerald-400 text-[#04120C] hover:shadow-[0_0_40px_rgba(16,185,129,0.5)]'
                      : 'bg-white/[0.04] hover:bg-white/[0.08] text-white border border-white/10'
                  }`}
                >
                  Get Started
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
